import React from "react"
import { Bar } from "react-chartjs-2"
import _ from "underscore"
import ChartDataLabels from "chartjs-plugin-datalabels"
import Chart from "chart.js/auto"
import { Tooltip } from "antd"

Chart.register(ChartDataLabels)

const Y_AXIS_INTERVALS = 25

function BarChart({
  icon,
  iconEvent,
  title,
  labels = [],
  datasets = [],
  onClick,
  mb = "0",
  stepSize = null,
  formatPercentage = false,
  legend = false,
  graphUrlBase64 = null,
  type,
  labelX = "",
  labelY = "",
  offset = 10,
  graphType = "",
}) {
  const adultColor = "#33485D"
  const getMaxValue = () => {
    const maxValues = datasets.map((dataset) => Math.max(...dataset.data))
    const absoluteMax = Math.max(...maxValues)
    return absoluteMax + absoluteMax * 0.05
  }

  // A stepSize of 1 creates one tick per student, and Chart.js then auto-skips ticks based on
  // the chart's pixel height, so the same data got different y-axis labels on pages where the
  // chart renders at a different width. Picking the step here keeps the axis identical everywhere.
  const getYAxis = () => {
    const axisTop = getMaxValue() + offset
    if (!stepSize) return { max: axisTop, step: null }

    const step = Math.max(stepSize, Math.floor(axisTop / Y_AXIS_INTERVALS))
    const dataMax = Math.max(...datasets.map((dataset) => Math.max(...dataset.data)))
    let max = Math.floor(axisTop / step) * step
    if (max <= dataMax) max += step
    return { max, step }
  }
  const yAxis = getYAxis()

  const curvedDatasets = datasets.map((dataset) => ({
    ...dataset,
    backgroundColor: type === "adult" ? adultColor : dataset.backgroundColor,
    borderRadius: {
      topLeft: 10,
      topRight: 10,
      bottomLeft: 0,
      bottomRight: 0,
    },
  }))

  const data = {
    labels,
    datasets: curvedDatasets,
  }

  const options = {
    onClick: (event, element) => {
      if (element.length && onClick) {
        if (graphType === "activity-grade") {
          const { datasetIndex } = element[0]
          const dataIndex = element[0].index
          const clickedLabel = labels[dataIndex]
          const clickedValue = datasets[datasetIndex].data[dataIndex]
          onClick({ label: clickedLabel, value: clickedValue })
        } else {
          onClick(element[0].index)
        }
      }
    },
    onHover: (event, chartElement) => {
      if (onClick) {
        event.native.target.style.cursor = chartElement[0]
          ? "pointer"
          : "default"
      }
    },

    plugins: {
      legend: {
        display: legend,
      },
      tooltip: {
        enabled: _.isNull(graphUrlBase64) ? true : graphUrlBase64.length > 0,
        callbacks: {
          label: (tooltipItem) => ` ${tooltipItem.dataset.label}: ${tooltipItem.formattedValue}${
            formatPercentage ? "%" : ""
          }`,
        },
      },

      datalabels: {
        display: true,
        color: "black",
        align: "end",
        anchor: "end",
        font: { size: "11" },
      },
    },

    scales: {
      x: {
        title: {
          display: true,
          text: labelX,
        },
      },
      y: {
        title: {
          display: true,
          text: labelY,
        },
        min: 0,
        max: yAxis.max,
        beginAtZero: true,
        ticks: {
          stepSize: yAxis.step,
          autoSkip: !yAxis.step,
          callback(value) {
            return Number(value).toFixed(0)
          },
        },
      },
    },
  }

  if (title) {
    options.plugins.title = {
      display: true,
      text: title,
    }
  }

  const hasData = datasets.some(
    (dataset) => dataset.data && dataset.data.some((val) => val > 0),
  )
  // react-chartjs-2 only registers inline plugins when the chart is first created, so a closure
  // over hasData would keep its first-render value (often empty, before the data loads) forever.
  // Options are updated on every render, so the plugin reads the current value from there.
  options.plugins.noDataText = { hasData }

  const noDataPlugin = {
    id: "noDataText",
    afterDraw(chart, args, pluginOptions) {
      if (!pluginOptions.hasData) {
        const { ctx, width, height } = chart
        ctx.save()
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.font = "16px sans-serif"
        ctx.fillStyle = "#aaa"
        ctx.fillText("No Data", width / 2, height / 2)
        ctx.restore()
      }
    },
  }

  return (
    <div className={`mb-${mb}`}>
      <Bar data={data} options={options} plugins={[noDataPlugin]} />
      <div
        onClick={() => {
          iconEvent()
        }}
        className="icon"
      >
        {icon && <Tooltip title="View Label Definitions">{icon}</Tooltip>}
      </div>
    </div>
  )
}

export default BarChart
